import { motion } from "framer-motion";
import { useAppearance } from "@/lib/appearance";

interface MeshGradientProps {
  className?: string;
}

/**
 * Layered "mesh" of 3 large blurred radial blobs in theme-aware colors with a
 * slow drift loop. Reduce-motion users see the same composition without the
 * drift animation.
 */
export function MeshGradient({ className = "" }: MeshGradientProps) {
  const { isReducedMotion } = useAppearance();

  // Use base motion props that are static when reduce-motion is on.
  const base = (translateX: number, translateY: number) => {
    if (isReducedMotion) {
      return {
        animate: { x: 0, y: 0 },
        transition: { duration: 0 },
      };
    }
    return {
      animate: { x: [0, translateX, 0], y: [0, translateY, 0] },
      transition: {
        duration: 22,
        repeat: Infinity,
        ease: "easeInOut" as const,
      },
    };
  };

  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      <motion.div
        className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full blur-3xl bg-primary/25"
        {...base(60, 40)}
      />
      <motion.div
        className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full blur-3xl bg-accent/40"
        {...base(-50, -30)}
      />
      <motion.div
        className="absolute -bottom-40 left-1/4 w-[560px] h-[560px] rounded-full blur-3xl bg-primary/15"
        {...base(40, -50)}
      />
    </div>
  );
}
